import { useCallback, useEffect, useMemo, useState } from 'react';
import OrganizationsList from './OrganizationsList';
import OrganizationJoin from './OrganizationJoin';
import OrganizationDetail from './OrganizationDetail';
import './OrganizationsX.css';
import { Activity, Building2, ChevronRight, Users, CreditCard, Globe } from 'lucide-react';
import OrganizationHeader from './OrganizationHeader';
import organizationsService from '../services/organizationsService';
import {
  useOrganizationsStore,
  setOrganizations,
  setOrganizationsLoaded,
  mergeTeamsForOrg,
} from '../store/organizationsStore';
import { useTeamsStore } from '../store/teamsStore';
import { useAuthStore } from '../store/authStore';
import './Organizations.css';
import { CreateOrganization } from './organizations/CreateOrganization';
import { ProfileTab } from './organizations/OrgProfileTab';
import { MembersTab } from './organizations/OrgMembersTab';
import { TeamsTab } from './organizations/OrgTeamsTab';
import { AuditTab } from './organizations/OrgAuditTab';
import { DomainsTab } from './organizations/OrgDomainsTab';
import { BillingSummaryTab } from './organizations/OrgBillingSummaryTab';

export default function Organizations() {
  const organizations = useOrganizationsStore((s) => s.organizations);
  const loaded = useOrganizationsStore((s) => s.loaded);
  const teams = useTeamsStore((s) => s.teams);
  const currentUserId = useAuthStore((s) => s.user?.id);

  const [view, setView] = useState('list');
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState('profile');
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  // From the store, so it survives leaving and returning to the page.
  const teamsByOrg = useOrganizationsStore((s) => s.teamsByOrg);

  const selected = useMemo(
    () => organizations.find((o) => String(o.id) === String(selectedId)) || null,
    [organizations, selectedId]
  );
  const canAdminister = selected?.role === 'owner' || selected?.role === 'admin';

  const reload = useCallback(async () => {
    try {
      setOrganizations(await organizationsService.getOrganizations());
    } catch {
      // Keep what is already on screen: a transient failure must not empty the
      // directory and make it look as though the organisations were deleted.
    } finally {
      setOrganizationsLoaded(true);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // Teams per organisation, for the chips on the list.
  //
  // Stale-while-revalidate: whatever was cached paints immediately and each
  // organisation's list is refreshed in the background, landing one at a time
  // as it arrives. Counts correct themselves without the page ever showing an
  // empty state it has already been past.
  //
  // Per organisation rather than one batched write, so a slow one does not
  // hold up the rest — and a failure leaves that organisation's cached teams
  // alone instead of blanking them.
  useEffect(() => {
    let cancelled = false;
    organizations.forEach(async (o) => {
      try {
        const teams = await organizationsService.getTeams(o.id);
        if (!cancelled) mergeTeamsForOrg(o.id, teams);
      } catch {
        // Keep the cached list. An organisation whose teams briefly fail to
        // load should look unchanged, not empty.
      }
    });
    return () => {
      cancelled = true;
    };
  }, [organizations]);

  if (!loaded) return <div className="orgx-none">Loading organizations…</div>;

  if (view === 'detail' && selected) {
    return (
      <OrganizationDetail
        organization={selected}
        onBack={() => setView('list')}
        onOpenSettings={() => setView('settings')}
      />
    );
  }

  if (view === 'settings' && selected) {
    return (
      <div className="orgx-scroll">
        <div className="orgx">
        <div className="orgx-crumbs">
          <button type="button" className="orgx-crumb-link" onClick={() => setView('list')}>
            Organizations
          </button>
          <ChevronRight size={13} aria-hidden />
          <button type="button" className="orgx-crumb-link" onClick={() => setView('detail')}>
            {selected.name}
          </button>
          <ChevronRight size={13} aria-hidden />
          <span>Settings</span>
        </div>

        <OrganizationHeader organization={selected} />

        <div className="org-tabs">
          {[
            ['profile', 'Profile', Building2],
            ['members', 'Members', Users],
            ['teams', 'Teams', Users],
            ['domains', 'Domains', Globe],
            ['audit', 'Activity', Activity],
            ['billing', 'Billing', CreditCard],
          ].map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              className={`org-tab${tab === key ? ' org-tab--on' : ''}`}
              onClick={() => setTab(key)}
            >
              <Icon size={13} aria-hidden /> {label}
            </button>
          ))}
        </div>

        <div className="org-tab-body">
          {tab === 'profile' && (
            <ProfileTab org={selected} canEdit={canAdminister} onSaved={reload} />
          )}
          {tab === 'members' && (
            <MembersTab org={selected} canEdit={canAdminister} currentUserId={currentUserId} />
          )}
          {tab === 'teams' && <TeamsTab org={selected} canEdit={canAdminister} myTeams={teams} />}
          {tab === 'domains' && <DomainsTab org={selected} canEdit={canAdminister} />}
          {tab === 'audit' && <AuditTab org={selected} />}
          {tab === 'billing' && <BillingSummaryTab org={selected} canEdit={canAdminister} />}
        </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <OrganizationsList
        organizations={organizations}
        teamsByOrg={teamsByOrg}
        onOpen={(o) => {
          setSelectedId(o.id);
          setView('detail');
        }}
        onManage={(o) => {
          setSelectedId(o.id);
          setTab('profile');
          setView('settings');
        }}
        onCreate={() => setCreating(true)}
        onJoin={() => setJoining(true)}
      />
      {creating && <CreateOrganization onClose={() => setCreating(false)} onCreated={reload} />}
      {joining && <OrganizationJoin onClose={() => setJoining(false)} onJoined={reload} />}
    </>
  );
}

